import type { Scene } from '@lumen5/luminary';
import { TOOL_NAMES } from 'js/src/agent/toolNames';
import { z } from 'zod';

import type { Conversation } from '../Conversation';
import { spawnChildConversation } from '../tools/ConversationTool';
import { Model, ReasoningEffort } from '../types';
import type { EnhancedToolResult, ToolDefinition, ToolExecutionContext } from '../types';
import { getActiveVideoRecipe, getSkillsInContext } from '../skills/loadedSkills';
import { DesignTool, type DesignToolArgs, designTargetSchemaFields } from './designTargeting';
import type { AssignmentInput, TechniqueAssignment } from './skills/suggestTechniques';
import {
    assignmentFor,
    drawsAMap,
    fallbackAssignment,
    shortlist,
    soleTechniqueAssignment,
} from './skills/suggestTechniques';
import { CHOOSER_TASK, chooseTechniques } from './skills/chooseTechniques';
import { techniquePolicyFor } from './skills/recipeTechniquePolicies';
import { scheduleRejection } from './phaseSchedule';
import { checkAgainstSource, SOURCE_CHECK_TASK } from './sourceCheck';
import type { CarryPose } from './seamCheck';
import {
    closingPoseOf,
    copyAvailability,
    describePose,
    isHardCut,
    movedSeamNote,
    placeholderFor,
    seamsAsPlayed,
    seamsRunningThrough,
} from './seamCheck';

import { markScenesBusy } from 'js/src/data/agent-activity';
import type { SceneHandoff, ScenePlan } from 'js/src/data/html-design';
import {
    getAssignedTechniques,
    getEarlierScenePlans,
    getHtmlDesignPlan,
    getHtmlDesignSystem,
    getSceneHandoffs,
    setHtmlDesignPlan,
} from 'js/src/data/html-design';
import { setSceneDurationUserOverride } from 'js/src/data/user-overrides';
import { checkpointVideo } from 'js/src/data/video-data';
import { saveRawLuminary, setSceneDuration } from 'js/src/luminary/utils';
import { isVoicedrivenScene } from 'js/src/luminary/voicedriven-scenes';
import { SubagentAccess } from 'js/src/agent/subagents/subagentAccess';
import { MediaRegistry } from 'js/src/agent/registries/MediaRegistry';

export type { ScenePlan } from 'js/src/data/html-design';

const planEntrySchema = z.object({
    scene_id: designTargetSchemaFields.scene_id.unwrap(),
    sceneDurationMillis: z
        .number()
        .describe(
            'How long the whole scene runs, in milliseconds. You pace the ' +
                'graphic within it when you author the timeline.',
        ),
    scenePlan: z
        .object({
            text: z
                .array(z.string(), {
                    // A chart plan tends to send its figures in `data` and leave this out, then,
                    // told only that an array was expected, resend the plan with `data` dropped.
                    error: (issue) =>
                        issue.input === undefined
                            ? "required even beside data: list the words the graphic shows, a chart's labels and figures included, or [] if it shows none"
                            : undefined,
                })
                .describe(
                    'The exact words of every text block the graphic shows — ' +
                        'one entry per block, written out, not described. Always ' +
                        'send this field: pass [] for a graphic that shows no text. ' +
                        'Read the list back before ' +
                        'submitting: no figure and no claim may appear in more ' +
                        'than one entry.',
                ),
            graphics: z
                .array(z.string())
                .describe(
                    'Each non-text element, one per entry (e.g. "person icon", ' +
                        '"filled person icon"). Each one has to show something nothing else on ' +
                        "screen does — any line drawn along a chart's series or across or " +
                        'between its bars (an arrow, a bracket, a guide line) restates what the ' +
                        'chart already shows, and reads as noise. A rise or fall is performed, ' +
                        'not drawn: a figure that falls counts down, a bar that shrinks shrinks, ' +
                        'a thing that travels moves — never a figure slid down the frame, since ' +
                        'position on screen is layout, not value. An arrow is for "leads to" or ' +
                        '"this one", never for "went up" or "went down".',
                ),
            layout: z
                .string()
                .describe('Where those elements sit, e.g. "text on left, graphic on ' + 'right".'),
            data: z
                .array(
                    z.object({
                        label: z.string().describe('What this series is, e.g. "balance by year".'),
                        values: z
                            .array(z.number())
                            .describe('The real figures, in plotting order.'),
                    }),
                )
                .optional()
                .describe(
                    'The figures the graphic plots — chart, bar set, counter or unit field — in ' +
                        'plotting order, as the narration gives them; its shape is checked ' +
                        'against them. A stand-in like 1,2,3 declares nothing: an illustrative ' +
                        'shape takes data-l5-not-data instead.',
                ),
            // A flat string rather than an object holding the look and the media id together: given
            // the object, a model wrote `phases` as a string swallowing every field after it.
            reproduces: z
                .string({
                    error: (issue) =>
                        issue.input === undefined
                            ? 'required: describe what this graphic recreates from the material the user gave you, or send null for one designed from scratch'
                            : undefined,
                })
                .nullable()
                .describe(
                    'When this graphic recreates something from material the user gave you — a ' +
                        'chart, diagram, timeline or layout on a document page, a slide or an ' +
                        'image — how it is drawn there: its shape, how its parts are arranged, ' +
                        'what stands out. The look, not the content — figures go in data and ' +
                        'words in text — e.g. "a ring with a third swept in navy, a lightbulb in ' +
                        'its hole, the big fraction captioned to its right". That look is what ' +
                        'the user asked for: keep it, with every part the source shows, and ' +
                        "restyle only into this video's design system — never trade it for a " +
                        'different kind of graphic for variety. Always send this field: null for ' +
                        'a graphic you are designing from scratch.',
                ),
            reproduces_media_id: z
                .string()
                .optional()
                .describe(
                    'With reproduces: the media id of the page screenshot, slide or image that ' +
                        'shows the source, exactly as the tool that gave it to you labelled it.',
                ),
            phases: z
                .array(
                    z.object({
                        at: z
                            .number()
                            .describe(
                                'When this phase begins, in seconds from the start of the scene — ' +
                                    'the same clock the narration timings use, so a phase can be ' +
                                    'placed on the word it illustrates, never before the ' +
                                    'narration has named it. The first phase is at 0.',
                            ),
                        beat: z
                            .string()
                            .describe(
                                "The moment this phase lands, in the narration's own words (e.g. " +
                                    '"...only three had shipped"). Plain description of the beat ' +
                                    'for a scene with no narration.',
                            ),
                        enters: z
                            .string()
                            .optional()
                            .describe(
                                'The ONE new element arriving in this phase. If a phase needs ' +
                                    'three new elements before it makes sense, it is a second ' +
                                    "idea wearing a phase's clothes — split it or cut it. Omit it " +
                                    'only for a phase that brings nothing new — a settle, or a ' +
                                    'rearrangement of what is already there — which then has to ' +
                                    'say what it does in transforms or exits.',
                            ),
                        transforms: z
                            .string()
                            .optional()
                            .describe(
                                'What is already on screen and changes to make room — only an ' +
                                    'element the arrival is read against, e.g. "the chart slides ' +
                                    'left so its callout can land beside it". This is what ' +
                                    'makes a phase a rearrangement rather than another entrance. ' +
                                    'Required on every phase after the first, unless that phase ' +
                                    'says what goes in exits.',
                            ),
                        exits: z
                            .string()
                            .optional()
                            .describe(
                                'What leaves in this phase. The default for anything that has ' +
                                    'made its point: once the narration has moved past a line or ' +
                                    'figure, it goes, rather than shrinking to stay on screen. ' +
                                    'Keep one only when what arrives makes no sense without it — ' +
                                    'the chart a callout points at, the figure a comparison is ' +
                                    'measured against. A headline or line the narration has ' +
                                    'already said is never that: it leaves rather than shrinking ' +
                                    'into a header.',
                            ),
                    }),
                )
                .describe(
                    'When the graphic advances, in order — its pacing across the scene. A phase ' +
                        'is not just an entrance: something arrives, and what has made its point ' +
                        'leaves or, when the arrival is read against it, transforms to make room. ' +
                        'A longer scene buys more phases of the SAME idea (a setup, the thing ' +
                        'that lands it, what it means), never a second idea. Roughly one phase ' +
                        'per 5s of scene, spread across it so the last one lands late, with its ' +
                        'move finished a second before the cut so its result can be taken in: a ' +
                        'schedule that resolves early leaves a stretch with nothing happening, ' +
                        'which is rejected. Only the first phase may simply arrive — every later ' +
                        'one says what the arrival costs the frame in exits or transforms, or it ' +
                        'is rejected.',
                ),
        })
        .describe('What the graphic is, and when it advances, across the whole scene.'),
});
type PlanEntry = z.infer<typeof planEntrySchema>;

export const writePlanSchema = z.object({
    plans: z
        .array(planEntrySchema)
        .min(1)
        .describe(
            'One entry per scene, each with its own scene_id. Plan every graphic in the video in ' +
                'one call, in play order: their techniques are chosen together, so the video is ' +
                'varied as a whole. To revise one plan, send just that scene.',
        ),
});
export type WritePlanArgs = z.infer<typeof writePlanSchema>;

const SCENE_PLAN_FIELDS = Object.keys(planEntrySchema.shape.scenePlan.shape);

/**
 * Moves plan fields sent beside `scenePlan` into it. Models regularly nest the plan one level off —
 * `text` next to `scenePlan`, or the whole plan flattened onto the arguments — and, told exactly
 * what is missing, spend a round trip resending the same plan. None of these names means anything
 * at the top level, and a field the plan already carries is left as sent.
 */
function nestPlanFields(args: Record<string, unknown>): Record<string, unknown> {
    const misplaced = SCENE_PLAN_FIELDS.filter((field) => field in args);
    if (misplaced.length === 0) {
        return args;
    }
    const sent = args.scenePlan;
    const scenePlan: Record<string, unknown> =
        sent && typeof sent === 'object' && !Array.isArray(sent) ? { ...sent } : {};
    const rest = { ...args };
    for (const field of misplaced) {
        if (!(field in scenePlan)) {
            scenePlan[field] = rest[field];
        }
        delete rest[field];
    }
    return { ...rest, scenePlan };
}

/** A single plan sent the way one used to be, with scene_id at the top, is a list of one. */
function asPlanList(args: Record<string, unknown>): Record<string, unknown> {
    if (!('plans' in args)) {
        return { plans: [nestPlanFields(args)] };
    }
    const { plans } = args;
    if (!Array.isArray(plans)) {
        return args;
    }
    return {
        ...args,
        plans: plans.map((entry) =>
            entry && typeof entry === 'object' && !Array.isArray(entry)
                ? nestPlanFields(entry as Record<string, unknown>)
                : entry,
        ),
    };
}

const VOICEOVER_TOLERANCE_RATIO = 0.15;
const VOICEOVER_TOLERANCE_FLOOR_MS = 500;

function voiceoverTolerance(sceneDurationMs: number): number {
    return Math.max(VOICEOVER_TOLERANCE_FLOOR_MS, sceneDurationMs * VOICEOVER_TOLERANCE_RATIO);
}

// The agent is told to branch on `approved`, so a rejection has to arrive in that shape rather than
// as prose it would have to interpret.
function denied(reason: string): EnhancedToolResult {
    return { result: JSON.stringify({ approved: false, reason }), isSuccess: false };
}

/** One refused plan turns the whole call away, so the techniques are still chosen for every scene
 *  together once they all pass. */
function refusalOf(refused: RefusedPlan[], planCount: number): EnhancedToolResult {
    if (planCount === 1) {
        const [{ refusal }] = refused;
        return refusal.asError ?? denied(refusal.reason);
    }
    return {
        result: JSON.stringify({
            approved: false,
            reason:
                `No plan in this call was approved: ${refused.length} of ${planCount} were ` +
                'refused. Fix those and send every plan again in one call.',
            refused: refused.map(({ sceneId, refusal }) => ({
                scene_id: sceneId,
                reason: refusal.reason,
            })),
        }),
        isSuccess: false,
    };
}

/**
 * Casing and punctuation only, so "6 WEEKS" and "6 weeks" are one line. A figure repeated inside a
 * longer line — "6 WEEKS" against "6 WEEKS TO PREPARE" — reads as different copy here and is not
 * reported; matching on contained figures would flag a value against every line that mentions it.
 */
function normalize(line: string): string {
    return line
        .toLowerCase()
        .replace(/[^a-z0-9%]+/g, ' ')
        .trim();
}

function words(text: string): string[] {
    return normalize(text).split(' ').filter(Boolean);
}

/**
 * Copy this plan puts on screen that an earlier graphic already shows. Not grounds for rejection —
 * a deliberate callback or a recurring header is the same shape — but nothing else in the build
 * sees it: every other check runs inside one graphic.
 */
function copyAlreadyPlanned(text: string[], earlier: ReadonlyArray<{ text: string[] }>): string[] {
    const seen = new Set(
        earlier
            .flatMap((plan) => plan.text)
            .map(normalize)
            .filter(Boolean),
    );
    return text.filter((line) => seen.has(normalize(line)));
}

/** A seam this graphic opens on, with where the graphic before leaves the carried element. */
interface IncomingCarry {
    handoff: SceneHandoff;
    /** Null until the graphic before is built and marks the element. */
    pose: CarryPose | null;
}

/**
 * The copy a carried element is showing as the graphic before ends that this plan leaves out. It is
 * on screen here from the first frame, so a plan without it gets the composition that carries it
 * rejected as unplanned text, after it has been built. Read off the element itself rather than
 * declared, because a declaration can name the wrong words — a label beside the element, say.
 */
function missingCarriedCopy(text: string[], incoming: IncomingCarry[]): string[] {
    const planned = text.map(normalize);
    return incoming.flatMap(({ pose }) =>
        (pose?.texts ?? []).filter(
            (copy) => normalize(copy) && !planned.some((line) => line.includes(normalize(copy))),
        ),
    );
}

function seamBrief(incoming: IncomingCarry[], outgoing: SceneHandoff[]): string {
    const lines: string[] = [];
    for (const { handoff, pose } of incoming) {
        const where = pose ? `, which ends at ${describePose(pose)}` : '';
        const { copyable, why } = copyAvailability(pose);
        const opening =
            !copyable && why
                ? `It ${why}, so it can't be copied in: build it yourself, marked ` +
                  `data-l5-carry="${handoff.id}" and opening at exactly that pose.`
                : `Put ${placeholderFor(handoff.id)}: it is replaced with that element exactly ` +
                  `as that graphic ends it, in canvas pixels. ` +
                  `Don't draw it yourself — animate the copy by its ids (extend it, move it, dim ` +
                  `or restyle its parts) and add your own layers over or around it where this ` +
                  `graphic needs more, without resetting it at 0.`;
        lines.push(
            `Opens on "${handoff.id}" (${handoff.element}), carried in from the graphic before${where}. ` +
                `${opening} The first phase develops it; it does not enter.`,
        );
    }
    for (const handoff of outgoing) {
        const copiedIn = incoming.find(
            // Refused copies are rebuilt and marked by hand; any other opens on the copy, built yet
            // or not.
            (carry) => carry.handoff.id === handoff.id && !copyAvailability(carry.pose).why,
        );
        if (copiedIn) {
            lines.push(
                `Hands "${handoff.id}" on to the next graphic. To hand it on as it opened, leave ` +
                    `the copy as it is — it already carries data-l5-carry="${handoff.id}". To hand ` +
                    `it on with what this graphic adds to it, put the placeholder and those ` +
                    `additions in one full-canvas wrapper (position:absolute; inset:0) marked ` +
                    `data-l5-carry="${handoff.id}": the next graphic copies the wrapper. Either way ` +
                    `add nothing inside the copy itself — an untouched copy is kept in step when ` +
                    `the graphic before changes — and leave it all on screen, settled, through ` +
                    `the last frame. Where the element moves as it grows, move the wrapper, so ` +
                    `the copy and what this graphic adds move as one.`,
            );
            continue;
        }
        lines.push(
            `Hands "${handoff.id}" (${handoff.element}) on to the next graphic: mark it ` +
                `data-l5-carry="${handoff.id}" and leave it on screen, settled, through the last ` +
                `frame — the next graphic opens on a copy of it. Where it is a group that stays ` +
                `together, a timeline with its dates or a field of dots, mark one wrapper around ` +
                `the whole group rather than its parts; in SVG, that is the whole <svg>.`,
        );
    }
    return lines.join('\n');
}

function runningThroughNote(id: string): string {
    return (
        `Handoff "${id}" was planned across the cut this graphic now sits in, so it skips this ` +
        `graphic and nothing checks it. To keep it running through, declare it out of the graphic ` +
        `before and out of this one with ${TOOL_NAMES.writeDesignSystem}, and plan this graphic to ` +
        `open on it.`
    );
}

function echoedCopyNote(echoed: string[]): string {
    if (echoed.length === 0) {
        return '';
    }
    const list = echoed.map((line) => `"${line}"`).join(', ');
    return (
        `${list} already appears on another graphic in this video, so the viewer reads it twice. ` +
        `Keep it only if the repeat is the point — a callback, or a header the video returns to. ` +
        `Otherwise cut it here and let the graphic that owns it carry it.`
    );
}

export interface WritePlanToolArgs extends DesignToolArgs {
    soleTechnique?: string;
}

export class WritePlanTool extends DesignTool<typeof writePlanSchema, WritePlanToolArgs> {
    private readonly soleTechnique?: string;
    private readonly disputedScenes = new Set<string>();
    private conversation?: Conversation;

    public attachToConversation(conversation: Conversation): void {
        this.conversation = conversation;
    }

    /** A tool-less conversation kept with this plan's tool call; null when there is no call to keep it with. */
    private startChildFor(
        toolCallId: string | undefined,
        config: { model: string; reasoningEffort: ReasoningEffort; task: string },
        systemMessage: string,
    ): Conversation | null {
        if (!this.conversation || !toolCallId) {
            return null;
        }
        return spawnChildConversation(this.conversation, toolCallId, {
            ...config,
            systemMessage,
            tools: [],
            requiresVideoState: false,
        });
    }

    public constructor(args: WritePlanToolArgs) {
        super(args);
        this.soleTechnique = args.soleTechnique;
    }

    protected override readonly schema = writePlanSchema;
    protected override readonly toolName = TOOL_NAMES.writePlan;
    public override readonly subagentAccess = SubagentAccess.EDITS_TARGET;
    protected override readonly toolDescription =
        'Submit your plans for the graphics in the video and have them reviewed — every scene in ' +
        'one call, so their techniques are chosen together. You MUST have an approved plan for a ' +
        `scene before you call ${TOOL_NAMES.writeHtmlAsset} for it. ` +
        'For one plan it returns {"approved": true|false, "notes": "...", "reason": "...", ' +
        '"techniques": {...}}; for several, {"plans": [...]} holding one of those per scene, each ' +
        'with its scene_id. If any plan is refused, none is approved: the call returns ' +
        '{"approved": false, "reason": "...", "refused": [...]} naming each refused scene and why. ' +
        'Fix those and send every plan again in one call; do not start authoring. When approved is ' +
        'true, "notes" may carry advice worth applying (often blank), ' +
        '"handoffs" (when present) says what this graphic opens on or leaves for the next one, and ' +
        '"techniques.required" names the design & motion skills this graphic is to be built from — ' +
        'an assignment, not a list of options. When you build that graphic, ' +
        `${TOOL_NAMES.loadSkill} the ones "techniques.instruction" asks you to load that are not ` +
        'already loaded above, implement them all, and only then write the HTML with ' +
        `${TOOL_NAMES.writeHtmlAsset}. ` +
        'sceneDurationMillis is how long the whole scene should play.';

    public getToolDisplayName(): string {
        return 'Planning the graphic';
    }

    protected override extendParameters(
        parameters: ToolDefinition['function']['parameters'],
    ): ToolDefinition['function']['parameters'] {
        return parameters;
    }

    protected override preprocessArgs(args: Record<string, unknown>): Record<string, unknown> {
        return asPlanList(args);
    }

    public get statusIcon(): string {
        return 'draft';
    }

    public async execute(
        args: WritePlanArgs,
        context: ToolExecutionContext,
    ): Promise<EnhancedToolResult> {
        const reviews = await this.reviewPlans(args.plans, context);
        const refused = reviews.filter((review): review is RefusedPlan => 'refusal' in review);
        if (refused.length > 0) {
            return refusalOf(refused, reviews.length);
        }
        const approved = reviews as ApprovedPlan[];
        const techniques = await this.approve(approved, context);
        const approvals = approved.map((plan) =>
            this.approvalOf({ ...plan, techniques: techniques.get(plan.scene.id) }),
        );
        return {
            result: JSON.stringify(
                approvals.length === 1
                    ? approvals[0]
                    : {
                          plans: approvals.map((approval, index) => ({
                              scene_id: approved[index].scene.id,
                              ...approval,
                          })),
                      },
            ),
            isSuccess: true,
        };
    }

    /**
     * Each plan held to the checks a plan meets on its own, in the order sent. The source checks ask
     * a model, so they run together, and only once every plan has passed the rest.
     */
    private async reviewPlans(
        entries: PlanEntry[],
        context: ToolExecutionContext,
    ): Promise<PlanReview[]> {
        const batch = new Map<string, ScenePlan>();
        const reviews: PlanReview[] = [];
        for (const entry of entries) {
            const review = await this.reviewOnItsOwn(entry, batch);
            if (!('refusal' in review)) {
                batch.set(review.scene.id, entry.scenePlan);
            }
            reviews.push(review);
        }
        if (reviews.some((review) => 'refusal' in review)) {
            return reviews;
        }
        return Promise.all(
            reviews.map((review) => this.checkSource(review as ApprovedPlan, context)),
        );
    }

    private async reviewOnItsOwn(
        entry: PlanEntry,
        batch: ReadonlyMap<string, ScenePlan>,
    ): Promise<PlanReview> {
        const refuse = (reason: string, asError?: EnhancedToolResult): PlanReview => ({
            sceneId: entry.scene_id,
            refusal: { reason, asError },
        });
        const resolved = this.resolveAuthoringTarget(entry.scene_id);
        if ('error' in resolved) {
            return refuse(resolved.error.result as string, resolved.error);
        }
        const { scene } = resolved;
        if (batch.has(scene.id)) {
            return refuse(
                `Scene ${entry.scene_id} is planned twice in this call. Send one plan per scene.`,
            );
        }

        const { sceneDurationMillis } = entry;
        if (!Number.isFinite(sceneDurationMillis) || sceneDurationMillis <= 0) {
            return refuse('sceneDurationMillis must be a positive number of milliseconds.');
        }

        const { reproduces, reproduces_media_id: source } = entry.scenePlan;
        if (reproduces && !source) {
            return refuse(
                'reproduces describes a source but reproduces_media_id is missing. Send the ' +
                    'media id of the screenshot or image showing it.',
            );
        }
        if (source !== undefined && !MediaRegistry.getInstance().hasItem(source)) {
            return refuse(
                `reproduces_media_id "${source}" is not a media id in this conversation. Use ` +
                    `the id the screenshot or image showing the source came back with.`,
            );
        }

        const { moved, ...planned } = seamsAsPlayed(
            this.luminary,
            getSceneHandoffs(this.getState(), scene.id),
        );
        const incoming: IncomingCarry[] = await Promise.all(
            planned.incoming.map(async (handoff) => ({
                handoff,
                pose: await closingPoseOf(this.luminary, handoff),
            })),
        );
        // By word, as a carried element splits its copy into one piece per text node, and a plan
        // line can join several of them.
        const carriedWords = new Set(
            incoming.flatMap(({ pose }) => (pose?.texts ?? []).flatMap((copy) => words(copy))),
        );
        const isCarried = (line: string): boolean =>
            words(line).length > 0 && words(line).every((word) => carriedWords.has(word));
        // A carry whose element can't be read yet leaves no way to tell its copy from a repeat.
        const reentering = incoming.some(({ pose }) => !pose)
            ? []
            : this.copyReenteringAtCut(scene, entry.scenePlan, batch).filter(
                  (line) => !isCarried(line),
              );
        // A note rather than a rejection: refused, the line gets cut to pass, and a past station
        // missing from a timeline is a worse defect than its label blinking at the cut.
        let reenteringNote = '';
        if (reentering.length > 0) {
            const lines = reentering.map((line) => `"${line}"`).join(', ');
            // With a handoff already on the seam, a new one is the wrong fix: what is missing is that
            // the carried element does not hold this copy, and widening it is done in that graphic.
            const fix = planned.incoming.length
                ? `This seam already carries ${planned.incoming
                      .map(({ id }) => `"${id}"`)
                      .join(
                          ', ',
                      )}, and that element doesn't hold it. If it belongs with the carried ` +
                  `element, mark the element that holds both in the graphic before — one wrapper ` +
                  `around, say, the rail and its labels — and plan this graphic to open on it.`
                : `Carry it across: call ${TOOL_NAMES.writeDesignSystem} again with this seam in ` +
                  `handoffs, then plan this graphic to open on it.`;
            reenteringNote =
                `${lines} is on screen in the graphic straight before this one, so bringing it in ` +
                `again here makes it vanish at the cut and come back. ${fix} Keep it in this plan ` +
                `either way: a line that blinks at the cut is better than one that goes missing.`;
        }

        const uncarried = missingCarriedCopy(entry.scenePlan.text, incoming);
        if (uncarried.length > 0) {
            return refuse(
                `The element this graphic opens on is showing ` +
                    `${uncarried.map((copy) => `"${copy}"`).join(', ')} as the graphic before ends, ` +
                    `so those words are on screen here and belong in text. Add them.`,
            );
        }

        const voicedriven = isVoicedrivenScene(scene);
        if (voicedriven) {
            const drift = Math.abs(sceneDurationMillis - scene.duration);
            if (drift > voiceoverTolerance(scene.duration)) {
                return refuse(
                    `This scene's duration is fixed at ${scene.duration}ms by its voiceover, ` +
                        `and the plan is built for ${sceneDurationMillis}ms. The timing isn't ` +
                        `yours to choose here — re-plan the graphic to fill ${scene.duration}ms, ` +
                        `and pass that as sceneDurationMillis. ` +
                        `If that leaves more time than the content needs, spend it developing what ` +
                        `is already on screen rather than adding to it.`,
                );
            }
        }

        // Against the duration the graphic will really play for: a voicedriven scene keeps its own,
        // which the tolerance above allows to differ slightly from what the plan was built for.
        // Checked before the duration is written, so a denied plan leaves the scene untouched.
        const scheduleFor = voicedriven ? scene.duration : sceneDurationMillis;
        const rejection = scheduleRejection(entry.scenePlan.phases, scheduleFor / 1000);
        if (rejection) {
            return refuse(rejection);
        }

        return {
            entry,
            scene,
            voicedriven,
            incoming,
            outgoing: planned.outgoing,
            moved,
            reentering,
            isCarried,
            notes: [reenteringNote],
        };
    }

    private async checkSource(
        plan: ApprovedPlan,
        context: ToolExecutionContext,
    ): Promise<PlanReview> {
        const { scenePlan } = plan.entry;
        const source = scenePlan.reproduces_media_id;
        const findings = await checkAgainstSource(
            scenePlan,
            (systemMessage) =>
                this.startChildFor(
                    context.toolCallId,
                    {
                        model: Model.GPT_5_6_LUNA,
                        reasoningEffort: ReasoningEffort.LOW,
                        task: SOURCE_CHECK_TASK,
                    },
                    systemMessage,
                ),
            context.parentActionGroup,
        );
        const sourceNotes: string[] = [];
        const departures = [...(findings?.wrong ?? []), ...(findings?.form ?? [])];
        if (departures.length) {
            const found = departures.join(' ');
            // Once per scene: the checker misreads too, and refusing the same plan twice would loop.
            if (!this.disputedScenes.has(plan.scene.id)) {
                this.disputedScenes.add(plan.scene.id);
                return {
                    sceneId: plan.entry.scene_id,
                    refusal: {
                        reason:
                            `Checked against ${source}, the figure this graphic recreates: ${found} ` +
                            `Plan it as the figure is — its figures, and its kind of marks in the ` +
                            `same arrangement — then send the plan again.`,
                    },
                };
            }
            sourceNotes.push(
                `Checked against ${source} again: ${found} Approved on your reading — if the ` +
                    `figure says otherwise, the graphic shows the viewer something it isn't.`,
            );
        }
        // Leaving part of a figure out can be an editing choice, so it is said rather than refused.
        if (findings?.missing.length) {
            sourceNotes.push(
                `${source} also shows what this plan leaves out: ${findings.missing.join(' ')} ` +
                    `Keep it out only if the graphic is better without it.`,
            );
        }
        return { ...plan, notes: [...plan.notes, ...sourceNotes] };
    }

    /** Writes the approved plans, and what each is to be built from, in play order. */
    private async approve(
        approved: ApprovedPlan[],
        context: ToolExecutionContext,
    ): Promise<Map<string, TechniqueAssignment>> {
        if (approved.length === 0) {
            return new Map();
        }
        const scenes = this.luminary.scenes;
        const inPlayOrder = [...approved].sort(
            (a, b) => scenes.indexOf(a.scene) - scenes.indexOf(b.scene),
        );
        const sceneIds = inPlayOrder.map(({ scene }) => scene.id);

        const timed = inPlayOrder.filter(({ voicedriven }) => !voicedriven);
        if (timed.length > 0) {
            // Mirrors set_scene_duration: mark it a user override so a later text-driven
            // recommended-duration recalc doesn't reset the value the plan was built against.
            for (const { scene, entry } of timed) {
                setSceneDuration(this.luminary, scene.id, entry.sceneDurationMillis);
            }
            this.dispatch(
                checkpointVideo({
                    video_data: saveRawLuminary(this.luminary),
                    systemTriggered: false,
                    parentActionGroup: context.parentActionGroup,
                }),
            );
            for (const { scene } of timed) {
                this.dispatch(
                    setSceneDurationUserOverride({
                        sceneId: scene.id,
                        isAutomatic: false,
                        systemTriggered: false,
                        parentActionGroup: context.parentActionGroup,
                    }),
                );
            }
        }

        // Read before these plans are stored, so a scene is not compared against itself. Carried
        // copy repeats by design, which is the one repeat the note can tell apart from an accident.
        const stored = getEarlierScenePlans(this.getState(), sceneIds);
        inPlayOrder.forEach((plan, index) => {
            if (plan.incoming.some(({ pose }) => !pose)) {
                return;
            }
            const before = inPlayOrder.slice(0, index).map(({ entry }) => entry.scenePlan);
            const echoed = copyAlreadyPlanned(plan.entry.scenePlan.text, [
                ...stored,
                ...before,
            ]).filter((line) => !plan.isCarried(line) && !plan.reentering.includes(line));
            plan.notes.push(echoedCopyNote(echoed));
        });

        const techniques = await this.assignTechniques(inPlayOrder, context);

        // After the assignment, so the store holds only other scenes while it is made. Remembered
        // so the composition the agent then authors can be checked against it: unplanned copy is
        // reported as a blocking finding by write_html_asset and edit_html_asset.
        inPlayOrder.forEach(({ scene, entry }, index) => {
            this.dispatch(
                setHtmlDesignPlan({
                    sceneId: scene.id,
                    plan: entry.scenePlan,
                    techniques: techniques[index].required.map(({ skill }) => skill),
                }),
            );
        });
        // An approved plan is where building starts, so the editor dims the scene from here.
        markScenesBusy(this.dispatch, sceneIds);

        return new Map(sceneIds.map((sceneId, index) => [sceneId, techniques[index]]));
    }

    private approvalOf(plan: ApprovedPlan & { techniques?: TechniqueAssignment }): {
        approved: true;
        notes: string;
        techniques?: TechniqueAssignment;
        handoffs?: string;
    } {
        const handoffs = seamBrief(plan.incoming, plan.outgoing);
        return {
            approved: true,
            notes: [
                ...plan.moved.map((handoff) => movedSeamNote(this.luminary, handoff)),
                ...seamsRunningThrough(
                    this.luminary,
                    plan.scene,
                    getHtmlDesignSystem(this.getState())?.handoffs ?? [],
                ).map(({ id }) => runningThroughNote(id)),
                ...plan.notes,
            ]
                .filter(Boolean)
                .join(' '),
            techniques: plan.techniques,
            ...(handoffs ? { handoffs } : {}),
        };
    }

    /**
     * Lines this plan shares with the graphic that plays straight into it. Only that one: a line
     * repeated further back is a callback, which the viewer sees return rather than blink.
     */
    private copyReenteringAtCut(
        scene: Scene,
        plan: ScenePlan,
        batch: ReadonlyMap<string, ScenePlan>,
    ): string[] {
        const scenes = this.luminary.scenes;
        const previous = scenes[scenes.indexOf(scene) - 1];
        if (!previous || !isHardCut(previous, scene)) {
            return [];
        }
        const previousPlan =
            batch.get(previous.id) ?? getHtmlDesignPlan(this.getState(), previous.id);
        const theirs = new Set((previousPlan?.text ?? []).map(normalize));
        return plan.text.filter((line) => normalize(line) && theirs.has(normalize(line)));
    }

    /** One assignment per plan, in the order given: chosen together, so the video is varied as a
     *  whole rather than one scene at a time. */
    private async assignTechniques(
        plans: ApprovedPlan[],
        context: ToolExecutionContext,
    ): Promise<TechniqueAssignment[]> {
        const state = this.getState();
        const messages = this.getConversationMessages?.() ?? [];
        const recipe = getActiveVideoRecipe(messages);
        const policy = techniquePolicyFor(recipe?.name);
        const sceneIds = plans.map(({ scene }) => scene.id);
        const alreadyAssigned = getAssignedTechniques(state, sceneIds);
        const alreadyLoaded = getSkillsInContext(messages);
        const frames = plans.map(({ entry }) => entry.scenePlan);
        const inputFor = (frame: ScenePlan, assigned: ReadonlySet<string>): AssignmentInput => ({
            policy,
            alreadyAssigned: assigned,
            phaseTimes: frame.phases.map(({ at }) => at),
            drawsObject: frame.graphics.length > 0,
            drawsMap: drawsAMap(frame),
            alreadyLoaded,
        });
        const { soleTechnique } = this;
        if (soleTechnique) {
            return frames.map((frame) =>
                soleTechniqueAssignment(soleTechnique, inputFor(frame, alreadyAssigned)),
            );
        }
        const { model } = context;
        // Low is the one level every model family's vocabulary shares (reasoning-off is not: Gemini
        // has no 'none', always-reasoning models reject it), and ranking a long library against a
        // brief benefits from a little thought without needing more.
        const chosen = model
            ? await chooseTechniques(
                  frames,
                  shortlist({ policy, alreadyAssigned }),
                  getEarlierScenePlans(state, sceneIds),
                  (systemMessage) =>
                      this.startChildFor(
                          context.toolCallId,
                          {
                              model,
                              reasoningEffort: ReasoningEffort.LOW,
                              task: CHOOSER_TASK,
                          },
                          systemMessage,
                      ),
                  context.parentActionGroup,
                  recipe,
                  policy,
              )
            : frames.map(() => []);
        // A scene the chooser left without picks falls back one at a time, each seeing what the
        // ones before it were given, so the fallbacks don't all land on the same technique.
        const assignedSoFar = new Set(alreadyAssigned);
        return frames.map((frame, index) => {
            const input = inputFor(frame, new Set(assignedSoFar));
            const assignment =
                chosen[index].length > 0
                    ? assignmentFor(chosen[index], input)
                    : fallbackAssignment(input);
            assignment.required.forEach(({ skill }) => assignedSoFar.add(skill));
            return assignment;
        });
    }
}

interface ApprovedPlan {
    entry: PlanEntry;
    scene: Scene;
    voicedriven: boolean;
    incoming: IncomingCarry[];
    outgoing: SceneHandoff[];
    moved: SceneHandoff[];
    reentering: string[];
    isCarried: (line: string) => boolean;
    notes: string[];
}

interface RefusedPlan {
    sceneId: string;
    refusal: {
        reason: string;
        /** The result a lone plan returns in place of a rejection, when it never reached review. */
        asError?: EnhancedToolResult;
    };
}

type PlanReview = ApprovedPlan | RefusedPlan;
